import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaArrowLeft, FaUser, FaBuilding, FaBriefcase, 
  FaMoneyBillWave, FaHandHoldingUsd
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../../utils/api';
import './AdvanceSalaryLog.css';

const AdvanceSalaryLog = () => {
  const navigate        = useNavigate();
  const { empId }       = useParams();
  const [employee, setEmployee] = useState(null);
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!empId) return;
    setLoading(true);
    api.get(`/employees/${empId}/advance-logs`)
      .then(r => {
        setEmployee(r.data.employee);
        // Sort logs ascending (oldest first) to calculate running totals properly
        const sortedLogs = (r.data.data || []).sort((a, b) => new Date(a.date) - new Date(b.date));
        setLogs(sortedLogs);
      })
      .catch(() => Swal.fire('Error', 'Failed to load advance logs.', 'error'))
      .finally(() => setLoading(false));
  }, [empId]);

  // Calculate grand total for the top stat card
  const totalAdvance = useMemo(() => logs.reduce((acc, l) => acc + l.amount, 0), [logs]);

  // Helper to render dynamic colored badges for payment types
  const renderPaymentBadge = (type) => {
    const t = (type || '').toLowerCase();
    let colorClass = 'default';
    if (t.includes('cash')) colorClass = 'cash';
    else if (t.includes('bank') || t.includes('transfer') || t.includes('net')) colorClass = 'bank';
    else if (t.includes('upi')) colorClass = 'upi';
    else if (t.includes('cheque')) colorClass = 'cheque';
    else if (t.includes('salary') || t.includes('deduction')) colorClass = 'deduction';
    else if (t.includes('reversal')) colorClass = 'reversal';
    
    return <span className={`advance-salary-log-pay-badge ${colorClass}`}>{type || '—'}</span>;
  };

  if (loading) {
    return (
      <div className="advance-salary-log-loader-container">
        <div className="advance-salary-log-spinner"></div>
        <p>Loading Logs...</p>
      </div>
    );
  }

  let runningTotal = 0;

  return (
    <div className="advance-salary-log-container">

      {/* ── Header ── */}
      <div className="advance-salary-log-header">
        <button className="advance-salary-log-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="advance-salary-log-title">
          {employee?.name ? `${employee.name}'s Advance Salary Log` : 'Advance Salary Log'}
        </h2>
      </div>

      {/* ── Employee Profile & Stats Section ── */}
      <div className="advance-salary-log-profile-section">
        
        {/* Theme-Consistent College Card */}
        <div className="advance-salary-log-college-card">
          <div className="advance-salary-log-college-icon">
            <FaBuilding />
          </div>
          <div className="advance-salary-log-college-text">
            <label>College Name</label>
            <h3>{employee?.college?.name || '—'}</h3>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="advance-salary-log-info-grid">
          
          <div className="advance-salary-log-info-card">
            <div className="advance-salary-log-icon-wrapper user"><FaUser /></div>
            <div className="advance-salary-log-info-text">
              <label>Employee Name</label>
              <span>{employee?.name || '—'}</span>
            </div>
          </div>

          <div className="advance-salary-log-info-card">
            <div className="advance-salary-log-icon-wrapper briefcase"><FaBriefcase /></div>
            <div className="advance-salary-log-info-text">
              <label>Department</label>
              <span>{employee?.department?.name || '—'}</span>
            </div>
          </div>

          <div className="advance-salary-log-info-card">
            <div className="advance-salary-log-icon-wrapper money"><FaMoneyBillWave /></div>
            <div className="advance-salary-log-info-text">
              <label>Monthly Salary</label>
              <span className="advance-salary-log-salary">₹ {(employee?.salary || employee?.monthlySalary || 0).toLocaleString()}</span>
            </div>
          </div>

          <div className="advance-salary-log-info-card highlight">
            <div className="advance-salary-log-icon-wrapper advance"><FaHandHoldingUsd /></div>
            <div className="advance-salary-log-info-text">
              <label>Current Advance Balance</label>
              <span className="advance-salary-log-total-advance">₹ {totalAdvance.toLocaleString()}</span>
            </div>
          </div>

        </div>
      </div>

      {/* ── Advance History Table ── */}
      <div className="advance-salary-log-table-container">
        <div className="advance-salary-log-table-header">
          <h3 className="advance-salary-log-table-title">Advance Payment Details</h3>
        </div>
        
        <div className="advance-salary-log-table-wrapper">
          <table className="advance-salary-log-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>
                <th>Amount</th>
                <th className="advance-salary-log-align-left">Narration</th>
                <th>Payment Type</th>
                <th>Debited Bank</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="advance-salary-log-empty">No advance logs found for this employee.</td>
                </tr>
              ) : logs.map((log, index) => {
                
                // Add to running total
                runningTotal += log.amount;

                // Determine if we should show a Sub-Total row here.
                // Rule: Show subtotal if the NEXT item has a different sign (+ or -), OR if this is the VERY LAST item.
                const nextLog = logs[index + 1];
                const isSignChange = nextLog && (Math.sign(log.amount) !== Math.sign(nextLog.amount));
                const isLastItem = index === logs.length - 1;
                const showSubTotal = isSignChange || isLastItem;

                return (
                  <React.Fragment key={log._id}>
                    <tr>
                      <td>{index + 1}</td>
                      <td className="advance-salary-log-date">
                        {new Date(log.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className={`advance-salary-log-amount ${log.amount < 0 ? 'deduct' : 'add'}`}>
                        {log.amount < 0 ? `- ₹ ${Math.abs(log.amount).toLocaleString()}` : `₹ ${log.amount.toLocaleString()}`}
                      </td>
                      <td className="advance-salary-log-align-left advance-salary-log-narration">
                        {log.narration || '—'}
                      </td>
                      <td>{renderPaymentBadge(log.paymentType)}</td>
                      <td className="advance-salary-log-ref">
                        {log.paymentType === 'Cash' 
                          ? '—' 
                          : (log.bank?.name && log.bank?.accountNumber 
                              ? `${log.bank.name} - ${log.bank.accountNumber}` 
                              : '—')
                        }
                      </td>
                    </tr>
                    
                    {/* Sub-Total Row */}
                    {showSubTotal && (
                      <tr className="advance-salary-log-subtotal-row">
                        <td colSpan="2" className="advance-salary-log-subtotal-label">Total Balance :</td>
                        <td className="advance-salary-log-subtotal-amount">₹ {runningTotal.toLocaleString()}</td>
                        <td colSpan="3"></td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default AdvanceSalaryLog;